import { ImoovaImage } from "@/app/Media/Components/Image";
import { Suspense, useState } from "react";
import { createPortal } from "react-dom";
import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import {
  receiptQuery,
  updateReceiptMutation,
} from "@/app/Refunds/GraphQL/refundMutations";
import { Currency, ReceiptFieldsFragment, ReceiptType } from "@/gql/graphql";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { DateInput } from "@/lib/Components/Form/Inputs/DateInput";
import { CurrencyTypeInput } from "@/app/Common/Components/CurrencyTypeInput";
import { ReceiptTypeInput } from "@/app/Refunds/Components/ReceiptTypeInput";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { useFormState } from "@/lib/Components/Form/Hooks/useFormState";
import { NumberInput } from "@/lib/Components/Form/Inputs/NumberInput";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { z } from "zod";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { Button } from "@/components/catalyst/button";
import { MaximizeIcon, RotateCwIcon, XIcon } from "lucide-react";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";

const receiptFormSchema = z.object({
  type: z.enum(ReceiptType),
  amount_inc_tax: z.number().min(1, "Amount is required"),
  amount_tax: z.number().min(0, "Tax amount is required"),
  claimable_percentage: z
    .number()
    .min(0, "Must be 0 or more")
    .max(100, "Must be 100 or less"),
  currency: z.enum(Currency),
  date: z
    .string()
    .min(1, "Date is required")
    .nullable()
    .refine((val) => val !== null && val.length > 0, "Date is required"),
});

type ReceiptFormValues = z.infer<typeof receiptFormSchema>;

type EditReceiptDialogProps = {
  receiptId: string;
};

export function EditReceiptDialog({ receiptId }: EditReceiptDialogProps) {
  return (
    <BaseDialog title="Edit Receipt" size="3xl">
      <Suspense fallback={<Spinner />}>
        <EditReceiptDialogContent receiptId={receiptId} />
      </Suspense>
    </BaseDialog>
  );
}

function EditReceiptDialogContent({ receiptId }: { receiptId: string }) {
  const { data } = useSuspenseGqlQuery(receiptQuery, { id: receiptId });
  const receipt = data.receipt;

  if (!receipt) {
    return <div>Receipt not found</div>;
  }

  return <EditReceiptForm receipt={receipt} />;
}

function EditReceiptForm({ receipt }: { receipt: ReceiptFieldsFragment }) {
  const { close } = useDialogState();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [rotation, setRotation] = useState(0);
  const { mutateAsync: updateReceipt } = useGqlMutation(updateReceiptMutation);

  const handleSubmit = async (values: ReceiptFormValues) => {
    if (!values.date) {
      return;
    }

    await updateReceipt({
      id: receipt.id,
      input: {
        type: values.type,
        amount_inc_tax: values.amount_inc_tax,
        amount_tax: values.amount_tax,
        claimable_percentage: values.claimable_percentage / 100,
        currency: values.currency,
        date: values.date,
      },
    });

    return close();
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const image = receipt.image;
  const isPdf = image?.mime_type === "application/pdf";

  return (
    <>
      <div className="grid grid-cols-[1fr_320px] gap-6">
        <div className="space-y-3">
          {image ? (
            isPdf ? (
              <iframe
                src={image.url}
                title="Receipt PDF"
                className="h-[500px] w-full rounded-lg border"
              />
            ) : (
              <div className="relative">
                <ImoovaImage
                  media={image}
                  alt="Receipt"
                  className="h-auto max-h-[500px] w-full rounded-lg border object-contain transition-transform"
                  style={{ transform: `rotate(${rotation}deg)` }}
                />
                <Button
                  outline
                  className="absolute right-2 top-2"
                  onClick={() => setIsFullscreen(true)}
                >
                  <MaximizeIcon data-slot="icon" />
                </Button>
              </div>
            )
          ) : (
            <div className="flex h-[500px] items-center justify-center rounded-lg border bg-zinc-100 dark:bg-zinc-800">
              <span className="text-zinc-400">No image</span>
            </div>
          )}
          {image && isPdf ? (
            <Button
              outline
              className="w-full"
              onClick={() =>
                window.open(image.url, "_blank", "noopener,noreferrer")
              }
            >
              <MaximizeIcon data-slot="icon" />
              Open PDF in new tab
            </Button>
          ) : null}
          {image && !isPdf ? (
            <Button outline className="w-full" onClick={handleRotate}>
              <RotateCwIcon data-slot="icon" />
              Rotate 90°
            </Button>
          ) : null}
        </div>

        <GenericForm<ReceiptFormValues>
          initialValues={{
            type: receipt.type,
            amount_inc_tax: receipt.amount_inc_tax,
            amount_tax: receipt.amount_tax,
            claimable_percentage: parseFloat(
              (receipt.claimable_percentage * 100).toFixed(2),
            ),
            currency: receipt.currency,
            date: receipt.date ?? null,
          }}
          onSubmit={handleSubmit}
          validationSchema={toFormikValidationSchema(receiptFormSchema)}
          className="flex flex-col"
        >
          <ReceiptFormFields />
          <DialogSubmitButton text="Save Changes" />
        </GenericForm>
      </div>

      {isFullscreen && image
        ? createPortal(
            <div
              className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90"
              onClick={() => setIsFullscreen(false)}
            >
              <Button
                outline
                className="absolute right-4 top-4 !border-white/30 !text-white hover:!bg-white/10"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsFullscreen(false);
                }}
              >
                <XIcon data-slot="icon" />
              </Button>
              <ImoovaImage
                media={image}
                alt="Receipt"
                className="max-h-[90vh] max-w-[90vw] object-contain transition-transform"
                style={{ transform: `rotate(${rotation}deg)` }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function ReceiptFormFields() {
  const { values } = useFormState<ReceiptFormValues>();

  return (
    <div className="space-y-4">
      <ReceiptTypeInput name="type" label="Type" />
      <CurrencyTypeInput name="currency" label="Currency" />
      <div>
        <CurrencyInput
          name="amount_inc_tax"
          label="Amount (inc. tax)"
          currency={values.currency ?? Currency.Aud}
        />
        <div className="mt-1">
          <CurrencyInput
            name="amount_tax"
            label="Tax"
            currency={values.currency ?? Currency.Aud}
            className="text-sm text-zinc-500"
          />
        </div>
      </div>
      <NumberInput
        name="claimable_percentage"
        label="Claimable %"
        inputProps={{ step: "0.01", min: "0", max: "100" }}
        suffix="%"
      />
      <DateInput name="date" label="Receipt Date" />
    </div>
  );
}
